/**
 * Client-side spam detection utilities for EstimateForm
 * Implements Phase 1 anti-spam protections: honeypot, timing, disposable email detection
 */

// Common disposable email domains (cached client-side for quick validation)
const KNOWN_DISPOSABLE_DOMAINS = new Set([
  'tempmail.com', 'guerrillamail.com', '10minutemail.com', 'mailinator.com',
  'throwaway.email', 'fakeinbox.com', 'sharklasers.com', 'guerrillamail.info',
  'grr.la', 'guerrillamail.biz', 'guerrillamail.de', 'guerrillamail.net',
  'guerrillamail.org', 'spam4.me', 'temp-mail.org', 'tempail.com',
  'tempr.email', 'discard.email', 'discardmail.com', 'disposablemail.com',
  'emailondeck.com', 'getnada.com', 'mohmal.com', 'mytemp.email',
  'temp-mail.io', 'tempmailo.com', 'yopmail.com', 'yopmail.fr',
  'mailnesia.com', 'mintemail.com', 'trashmail.com', 'trashmail.net',
  'wegwerfmail.de', 'spamgourmet.com', 'maildrop.cc', 'mailcatch.com',
  'getairmail.com', 'dropmail.me', 'emailfake.com', 'fakemailgenerator.com',
  'tempinbox.com', 'binkmail.com', 'mailsac.com', 'spambox.us',
  'tempmailaddress.com', 'burnermail.io', '33mail.com', 'crazymailing.com',
  'inboxbear.com'
]);

/**
 * Check if email domain is a known disposable email provider
 */
export const isDisposableEmail = (email: string): boolean => {
  if (!email || !email.includes('@')) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  return KNOWN_DISPOSABLE_DOMAINS.has(domain);
};

/**
 * Get email domain for additional server-side checking
 */
export const getEmailDomain = (email: string): string | null => {
  if (!email || !email.includes('@')) return null;
  return email.split('@')[1]?.toLowerCase() || null;
};

/**
 * Calculate form timing in seconds
 */
export const calculateFormTiming = (loadedAt: number): number => {
  return Math.round((Date.now() - loadedAt) / 1000);
};

/**
 * Check if form was filled suspiciously fast (under 5 seconds is nearly impossible for humans)
 */
export const isSuspiciouslyFast = (formTimeSeconds: number): boolean => {
  return formTimeSeconds < 5;
};

/**
 * Build submission metadata for spam detection
 */
export interface SubmissionMetadata {
  form_loaded_at: string;
  form_time_seconds: number;
  honeypot_value: string;
  user_agent: string;
  screen_resolution: string;
  timezone: string;
  language: string;
  cookies_enabled: boolean;
  local_time: string;
}

export const buildSubmissionMetadata = (
  formLoadedAt: number,
  honeypotValue: string
): SubmissionMetadata => {
  return {
    form_loaded_at: new Date(formLoadedAt).toISOString(),
    form_time_seconds: calculateFormTiming(formLoadedAt),
    honeypot_value: honeypotValue,
    user_agent: navigator.userAgent,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    cookies_enabled: navigator.cookieEnabled,
    local_time: new Date().toISOString(),
  };
};

/**
 * Calculate client-side spam score (0-100)
 * Higher score = more likely spam
 */
export const calculateClientSpamScore = (
  formTimeSeconds: number,
  honeypotTriggered: boolean,
  isDisposable: boolean
): number => {
  let score = 0;
  
  // Honeypot triggered = definitely a bot
  if (honeypotTriggered) score += 100;
  
  // Disposable email = very suspicious
  if (isDisposable) score += 50;
  
  // Form filled too fast
  if (formTimeSeconds < 3) score += 40;
  else if (formTimeSeconds < 5) score += 25;
  else if (formTimeSeconds < 10) score += 10;
  
  return Math.min(score, 100);
};

/**
 * Determine if submission should be blocked based on client score
 */
export const shouldBlockSubmission = (clientScore: number): boolean => {
  // Block if honeypot triggered (score 100) or very suspicious (score >= 75)
  return clientScore >= 75;
};
